import { Type } from '@angular/core';
import { NxStatusIconType } from '@aposin/ng-aquila/icon';
import { Dayjs } from 'dayjs';

import { ControlDataComponent } from '#components/control-data/control-data.component';
import { CompanyGradeExpirationCode } from '#data/CompanyGradeExpirationCode.enum';
import { ServicesInjector } from '#data/ServicesInjector';
import { ControllerDetails, ControllerDetailsWithResponse, hasCheckData } from '#services/cve/ControllerDetails';
import { VettingIteration } from '#services/cve/VettingIteration';

import { GERLCompany } from './Gerl.model';

export interface Notification {
  context: NotificationContext;
  message: Type<unknown>;
  messageInputs?: Record<string, unknown>;
}
export enum NotificationContext {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  NONE = 'none',
}

export type NotificationsMap = Partial<Record<'modal' | 'escalationModal' | 'details', Notification[]>>;

interface DataOptions {
  style?: 'light' | 'regular' | 'semibold' | 'bold';
  iconName?: string;
}
type Data = DataOptions & { mustTranslate?: boolean; text: string };

export interface FormattedPublicData {
  i18n: string;
  testId?: string;
  data: Data[];
}

export interface TradeSector {
  code: string;
  isMain: boolean;
  isRestricted?: boolean;
}

export interface ControllerDataMapping {
  EconomicSanctions: never;
  PoliticalExposedPerson: never;
  CompanyGrade: {
    grade?: {
      lastRefreshDate: string;
      gradeCode: string;
    };
    expirationCode: CompanyGradeExpirationCode;
  };
  Gerl: {
    gerl?: {
      lastUpdateDate?: string;
      isGerlCompliant: boolean;
      isUpGerlCompliant?: boolean;
      isUltimateParent?: boolean;
      isRelatedCompaniesListed: boolean;
      companies: GERLCompany[];
    };
  };
  TradeSector: { tradeSectors: TradeSector[] };
  EnergyPolicy: { tradeSectors: TradeSector[] };
  SensitiveBusiness: { tradeSectors: TradeSector[] };
  SensitiveCountry: { countryCode: string };
  ReputationalRisk: never;
  CompanyConsortOrCoop: never;
  AccountDetails: never;
  AccountOwner: never;
  PreviousNegociation: never;
  PreviousContract: never;
  ProspectLocation: never;
  BuExchangeGroup: never;
  MncList: never;
  UnpaidPremiumAndFees: never;
}

export type ControllerCode = keyof ControllerDataMapping;

export interface MicroControllerConfig<T extends ControllerCode = ControllerCode> {
  i18n: string;
  code: T;
  manual?: boolean;
  hasQuiz?: boolean;
  additionalCheck?: {
    modalComponent: Type<unknown>;
    modalInputData: unknown;
    isAllowed: <M extends MicroController<T>>(data: M) => boolean;
    isRequiredForUpdate: <M extends MicroController<T>>(data: M) => boolean;
  };
  customUpdate?: {
    modalComponent: Type<unknown>;
  };
  customEscalation?: {
    modalComponent: Type<unknown> & { headingId: string };
  };
  isRerunCheckDisplayed?: <M extends MicroController<T>>(data: M) => boolean;
  hideIsExpiredWarning?: boolean;
  hasEscalationEmailInput?: boolean;
  isExcludedForBuCodes?: string[];
  specificNotificationsDisplay?: <M extends MicroController<T>>(
    data: M,
    services: ServicesInjector,
    iterationData?: VettingIteration,
  ) => NotificationsMap;
  publicDataFormatter?: ControllerDataMapping[T] extends never
    ? undefined
    : <V extends ControllerDataMapping[T]>(v: V, services: ServicesInjector) => Promise<FormattedPublicData[]>;
  commentTemplate?: <M extends MicroController<T>>(data: M) => string | undefined;
  publicDataComponent?: Type<ControlDataComponent>;
  escalationResponseDuration?: Parameters<Dayjs['add']>;
}

const checkConfigCode = <T extends ControllerCode>(
  config: MicroControllerConfig,
  code: T,
): config is MicroControllerConfig<T> => {
  return config.code === code;
};

export const findControlConfig = <T extends ControllerCode>(
  configs: MicroControllerConfig[],
  controlCode: T,
): MicroControllerConfig<T> | undefined => {
  return configs.find((config) => checkConfigCode(config, controlCode));
};

export interface AdditionalCheckModalData<T> {
  performAdditionalCheck: (requestData: T) => Promise<void>;
  setShouldClose: (val: boolean) => void;
  closeModal: () => void;
}

export enum DocumentStatus {
  NOT_UPLOADED = 'NOT_UPLOADED',
  UPLOADED = 'UPLOADED',
  IN_PROCESSING = 'IN_PROCESSING',
  REPROCESSING = 'REPROCESSING',
  AVAILABLE = 'AVAILABLE',
  PROCESSING_FINISHED = 'PROCESSING_FINISHED',
  LEGAL_HOLD = 'LEGAL_HOLD',
  PROCESSING_FAILED = 'PROCESSING_FAILED',
  FAILED = 'FAILED',
  TIMEOUT = 'TIMEOUT',
  UPLOAD_FAILED = 'UPLOAD_FAILED',
  DELETED_LOGICALLY = 'DELETED_LOGICALLY',
  DELETED_PHYSICALLY = 'DELETED_PHYSICALLY',
  DELETED_GDPR = 'DELETED_GDPR',
}

interface DocumentStatusMapping {
  statuses: DocumentStatus[];
  iconType?: NxStatusIconType | 'pending';
  i18n: string;
  isDownloadAvailable?: boolean;
}

export const pendingDocumentStatuses = [
  DocumentStatus.NOT_UPLOADED,
  DocumentStatus.UPLOADED,
  DocumentStatus.IN_PROCESSING,
  DocumentStatus.REPROCESSING,
];

export const documentStatusMapping: DocumentStatusMapping[] = [
  {
    statuses: pendingDocumentStatuses,
    iconType: 'pending',
    i18n: 'document.pending-status',
  },
  {
    statuses: [DocumentStatus.AVAILABLE, DocumentStatus.PROCESSING_FINISHED, DocumentStatus.LEGAL_HOLD],
    iconType: 'success',
    i18n: 'document.success-status',
    isDownloadAvailable: true,
  },
  {
    statuses: [
      DocumentStatus.PROCESSING_FAILED,
      DocumentStatus.FAILED,
      DocumentStatus.TIMEOUT,
      DocumentStatus.UPLOAD_FAILED,
    ],
    iconType: 'error',
    i18n: 'document.error-status',
  },
  {
    statuses: [DocumentStatus.DELETED_LOGICALLY, DocumentStatus.DELETED_PHYSICALLY, DocumentStatus.DELETED_GDPR],
    iconType: 'warning',
    i18n: 'document.deleted-status',
  },
];

export const documentStatusesWithAvailableDownload = documentStatusMapping
  .filter((m) => m.isDownloadAvailable)
  .flatMap((m) => m.statuses);

export interface DocumentMetadata {
  documentUid: string;
  documentStatusCode: DocumentStatus;
  creationDateTime: string;
  fileName: string;
  mimeTypeCode: string;
}

interface MicroControllerState<T extends ControllerCode> {
  inError: boolean;
  loadingError?: { traceparent?: string };
  isLoading?: boolean;
  reviewDate?: string;
  controllerDetails: ControllerDetails<T>;
  isExpired?: boolean;
  escalationContact?: string;
}

export type MicroController<T extends ControllerCode = ControllerCode> = MicroControllerConfig<T> &
  MicroControllerState<T>;

export const getMicroControllerData = <T extends ControllerCode>(
  microController: MicroController<T>,
): ControllerDataMapping[T] | undefined => {
  if (!hasCheckData(microController.controllerDetails)) {
    return;
  }
  return getMicroControllerDataFromDetails<T>(microController.controllerDetails);
};

export const getMicroControllerDataFromDetails = <T extends ControllerCode>(
  controllerDetails: ControllerDetailsWithResponse<T>,
): ControllerDataMapping[T] => {
  return controllerDetails.check.controlData as ControllerDataMapping[T];
};
